import { useEffect, useRef, useState, type DragEvent as ReactDragEvent, type PointerEvent as ReactPointerEvent } from 'react';
import { CG_TEMPLATES, cgTemplate, strapTemplateHasDesign, type CueKind } from '@airpost/shared';
import { insertionAt, moveWithinTail, tailSpot } from '../dnd.js';
import { t } from '../i18n.js';
import type { CueSlot } from '../storyboard-plugin.js';
import { StrapArtView } from '../strap-view.js';
import { hhmmss } from '../util.js';

export { PreviewDialog } from './rundown-dialogs.js';

/**
 * The storyboard panel beside the script: the cues of the story in playing order, like a playlist.
 * A video cue is a clip of the playout library (with preview) or a virtual code until the file exists;
 * a CG cue is one of AirPost's title types with its lines and a schematic preview of the strap.
 * The order is the order of the headings in the script; cues added by hand play after them, and those
 * are reordered by dragging (or with the arrows): a drop above the anchored ones snaps to the first manual spot.
 */

export interface CueDto {
  id: string;
  storyId: string;
  position: number;
  kind: CueKind;
  anchor: string | null;
  label: string;
  mediaAssetId: string | null;
  mediaCode: string | null;
  inMs: number | null;
  outMs: number | null;
  template: string | null;
  lines: string[];
  durationMs: number | null;
  media: { id: string; title: string; filename: string; durationMs: number; status: string } | null;
  /** The edit (Μοντάζ) of the clip the storyline plays, when there is one. */
  edit: { id: string; title: string; rendered: boolean; durationMs: number | null; renderedAt: string | null; plain: boolean } | null;
}

/** A cue as the editor sends it: an id for an existing one; `mediaCode: 'auto'` reserves the next virtual code. */
export interface CueDraft {
  id?: string;
  kind: CueKind;
  anchor: string | null;
  label: string;
  mediaAssetId: string | null;
  mediaCode: string | null;
  /** A clip's in and out points; on a CG cue, `inMs` is the second of the box before it at which the title comes in. */
  inMs?: number | null;
  outMs?: number | null;
  template: string | null;
  lines: string[];
  durationMs: number | null;
}

export const toDraft = (c: CueDto): CueDraft => ({ id: c.id, kind: c.kind, anchor: c.anchor, label: c.label, mediaAssetId: c.mediaAssetId, mediaCode: c.mediaCode, inMs: c.inMs, outMs: c.outMs, template: c.template, lines: c.lines, durationMs: c.durationMs });

/** Playing order: anchored cues as their headings come in the script, then cues whose heading is gone, then the manual ones. */
export function orderCues<T extends { anchor: string | null; position: number }>(cues: T[], slots: CueSlot[]): T[] {
  const slotIndex = new Map(slots.map((s, i) => [s.key, i]));
  const rank = (c: T) => (c.anchor ? (slotIndex.has(c.anchor) ? slotIndex.get(c.anchor)! : 10_000 + c.position) : 20_000 + c.position);
  return [...cues].sort((a, b) => rank(a) - rank(b) || a.position - b.position);
}

export function templateName(key: string | null, locale: string): string {
  const tpl = cgTemplate(key);
  return tpl ? (locale === 'en' ? tpl.en : tpl.el) : '';
}

const sec = (ms: number) => hhmmss(Math.round(ms / 1000));

/** The title as it will sit on the picture: a lower third in the station's design (ADR 083), the others as a schematic of the words and their place. */
export function CgPreview({ template, lines }: { template: string | null; lines: string[] }) {
  const tpl = cgTemplate(template) ?? CG_TEMPLATES[0];
  if (strapTemplateHasDesign(tpl.key)) return <StrapArtView lines={lines.slice(0, tpl.lines)} />;
  const shown = lines.slice(0, tpl.lines).filter((l) => l.trim());
  return (
    <div className={`cg-preview ${tpl.key}`} aria-hidden="true">
      <div className="strap">
        {shown.length === 0 && <div className="l1">…</div>}
        {shown.map((l, i) => (
          <div key={i} className={i === 0 ? 'l1' : 'l2'}>
            {l}
          </div>
        ))}
      </div>
    </div>
  );
}

interface Props {
  cues: CueDto[];
  slots: CueSlot[];
  locale: string;
  readOnly: boolean;
  busy: boolean;
  notice: string | null;
  focusId: string | null;
  hasStory: boolean;
  onCommit: (next: CueDraft[]) => void;
  /** Open the clip chooser: for a cue, or (null) for a new manual video cue. */
  onChoose: (cue: CueDto | null) => void;
  onPreview: (cue: CueDto) => void;
  onClose: () => void;
}

type CgDraft = { template: string | null; lines: string[]; durationMs: number | null };

/** The title type, its time on air, its lines, and the schematic of the strap. */
function CgCueEditor({ draft, locale, disabled, onEdit, onFlush }: { draft: CgDraft; locale: string; disabled: boolean; onEdit: (d: CgDraft) => void; onFlush: () => void }) {
  const tpl = cgTemplate(draft.template) ?? CG_TEMPLATES[0];
  const lines = Array.from({ length: tpl.lines }, (_, k) => draft.lines[k] ?? '');
  return (
    <div>
      <div className="sb-row">
        <select value={tpl.key} disabled={disabled} onChange={(e) => onEdit({ ...draft, template: e.target.value, durationMs: cgTemplate(e.target.value)?.defaultMs ?? draft.durationMs })} title={t('cueTemplate')}>
          {CG_TEMPLATES.map((x) => (
            <option key={x.key} value={x.key}>
              {locale === 'en' ? x.en : x.el}
            </option>
          ))}
        </select>
        <input className="dur" type="number" min={0} max={3600} value={Math.round((draft.durationMs ?? 0) / 1000)} disabled={disabled} onChange={(e) => onEdit({ ...draft, durationMs: Math.max(0, Number(e.target.value) || 0) * 1000 })} title={t('cueDurationS')} />
        <span className="muted">″</span>
      </div>
      <div className="sb-lines">
        {lines.map((l, k) => (
          <input key={k} value={l} maxLength={200} disabled={disabled} placeholder={`${t('cueLine')} ${k + 1}`} onChange={(e) => onEdit({ ...draft, lines: lines.map((x, m) => (m === k ? e.target.value : x)) })} onBlur={onFlush} />
        ))}
      </div>
      <CgPreview template={tpl.key} lines={lines} />
    </div>
  );
}

export function StoryboardPanel({ cues, slots, locale, readOnly, busy, notice, focusId, hasStory, onCommit, onChoose, onPreview, onClose }: Props) {
  const ordered = orderCues(cues, slots);
  const slotKeys = new Set(slots.map((s) => s.key));
  // CG text is edited here and sent after a pause, so every keystroke does not round-trip
  const [drafts, setDrafts] = useState<Record<string, CgDraft>>({});
  const timer = useRef<number | null>(null);
  const pending = useRef<{ id: string; draft: CgDraft } | null>(null);
  const flush = () => {
    const p = pending.current;
    if (!p) return;
    pending.current = null;
    onCommit(ordered.map((c) => (c.id === p.id ? { ...toDraft(c), ...p.draft } : toDraft(c))));
    setDrafts((d) => {
      const n = { ...d };
      delete n[p.id];
      return n;
    });
  };
  const edit = (id: string, draft: CgDraft) => {
    setDrafts((d) => ({ ...d, [id]: draft }));
    pending.current = { id, draft };
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(flush, 700);
  };
  useEffect(() => () => {
    if (timer.current) window.clearTimeout(timer.current);
  }, []);
  useEffect(() => {
    if (!focusId) return;
    document.getElementById(`sb-cue-${focusId}`)?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [focusId]);

  const drafts_ = (c: CueDto): CgDraft => drafts[c.id] ?? { template: c.template, lines: c.lines, durationMs: c.durationMs };
  const list = () => ordered.map(toDraft);
  const remove = (id: string) => onCommit(list().filter((c) => c.id !== id));
  const swap = (i: number, j: number) => {
    const l = list();
    if (j < 0 || j >= l.length || l[i]!.anchor || l[j]!.anchor) return;
    [l[i], l[j]] = [l[j]!, l[i]!];
    onCommit(l);
  };
  const addVirtual = () => onCommit([...list(), { kind: 'video', anchor: null, label: t('storyVideo'), mediaAssetId: null, mediaCode: 'auto', template: null, lines: [], durationMs: null }]);
  const addCg = () => {
    const tpl = CG_TEMPLATES[0];
    onCommit([...list(), { kind: 'cg', anchor: null, label: 'CG', mediaAssetId: null, mediaCode: null, template: tpl.key, lines: [''], durationMs: tpl.defaultMs }]);
  };
  const videoMs = ordered.reduce((a, c) => a + (c.kind === 'video' ? (c.media?.durationMs ?? 0) : 0), 0);
  const disabled = readOnly || busy || !hasStory;

  // ── drag and drop of the manual cues ───────────────────────────────────────────
  const isManual = (c: { anchor: string | null }) => !c.anchor;
  const [drag, setDrag] = useState<{ id: string; at: number | null } | null>(null);
  const [armed, setArmed] = useState<string | null>(null);
  const listRef = useRef<HTMLOListElement>(null);
  useEffect(() => {
    if (armed === null) return;
    const clear = () => setArmed(null);
    window.addEventListener('pointerup', clear);
    window.addEventListener('pointercancel', clear);
    return () => {
      window.removeEventListener('pointerup', clear);
      window.removeEventListener('pointercancel', clear);
    };
  }, [armed]);
  const dragFrom = drag ? ordered.findIndex((c) => c.id === drag.id) : -1;
  const snapped = drag && drag.at !== null ? tailSpot(ordered, isManual, drag.at) : null;
  const bar = snapped !== null && dragFrom >= 0 && snapped !== dragFrom && snapped !== dragFrom + 1 ? snapped : null;
  const bounds = () => Array.from(listRef.current?.querySelectorAll<HTMLElement>(':scope > li') ?? [], (el) => el.getBoundingClientRect());
  const arm = (id: string) => (e: ReactPointerEvent<HTMLDivElement>) => {
    if (disabled || (e.target as HTMLElement).closest('input, button, select, textarea, label, a')) return;
    setArmed(id);
  };
  const start = (id: string) => (e: ReactDragEvent<HTMLLIElement>) => {
    if (e.target !== e.currentTarget) return;
    if (disabled || armed !== id) return e.preventDefault();
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', id);
    setDrag({ id, at: null });
  };
  const endDrag = () => {
    setDrag(null);
    setArmed(null);
  };
  const over = (e: ReactDragEvent<HTMLOListElement>) => {
    if (!drag) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    const at = insertionAt(e.clientY, bounds());
    setDrag((d) => (d && d.at !== at ? { ...d, at } : d));
  };
  const leave = (e: ReactDragEvent<HTMLOListElement>) => {
    if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
    setDrag((d) => (d && d.at !== null ? { ...d, at: null } : d));
  };
  const drop = (e: ReactDragEvent<HTMLOListElement>) => {
    if (!drag) return;
    e.preventDefault();
    const from = ordered.findIndex((c) => c.id === drag.id);
    const next = from >= 0 ? moveWithinTail(list(), isManual, from, drag.at ?? insertionAt(e.clientY, bounds())) : null;
    if (next && next.some((c, i) => c.id !== ordered[i]!.id)) onCommit(next);
    endDrag();
  };

  return (
    <aside className="sb-panel">
      <div className="media-panel-head">
        <span>
          🎬 {t('storyboard')} <span style={{ opacity: 0.8 }}>{ordered.length}</span>
        </span>
        <button type="button" className="media-panel-close" onClick={onClose} title={t('close')}>
          ✖
        </button>
      </div>
      {!hasStory && <p className="muted small" style={{ padding: '6px 10px 0' }}>{t('storyVideoSaveFirst')}</p>}
      {ordered.length === 0 && hasStory && <p className="muted small" style={{ padding: '8px 10px 0', lineHeight: 1.45 }}>{t('storyboardEmpty')}</p>}
      <ol ref={listRef} className="sb-list" onDragEnter={over} onDragOver={over} onDragLeave={leave} onDrop={drop}>
        {ordered.map((c, i) => {
          const orphan = !!c.anchor && !slotKeys.has(c.anchor);
          const manual = !c.anchor;
          const prevManual = i > 0 && !ordered[i - 1]!.anchor;
          const nextManual = i < ordered.length - 1 && !ordered[i + 1]!.anchor;
          return (
            <li
              key={c.id}
              id={`sb-cue-${c.id}`}
              className={`sb-cue ${c.kind}${focusId === c.id ? ' focus' : ''}${drag?.id === c.id ? ' dragging' : ''}${bar === i ? ' drop-before' : ''}${bar === ordered.length && i === ordered.length - 1 ? ' drop-after' : ''}`}
              draggable={manual && !disabled && (armed === c.id || drag?.id === c.id)}
              onDragStart={start(c.id)}
              onDragEnd={endDrag}
            >
              <div className={`sb-cue-head${manual && !disabled ? ' grab' : ''}`} onPointerDown={manual ? arm(c.id) : undefined}>
                {manual && !disabled && (
                  <span className="grip" title={t('cueDragManual')} aria-hidden="true">
                    ⠿
                  </span>
                )}
                <span className="n">{i + 1}</span>
                <span>{c.kind === 'video' ? '🎬' : '🅒'}</span>
                <span className="label" title={c.anchor ?? ''}>
                  {c.label || (c.kind === 'video' ? t('storyVideo') : 'CG')}
                </span>
                {orphan && (
                  <span className="status warn" title={t('cueOrphan')}>
                    ?
                  </span>
                )}
                {manual && (
                  <span className="muted" title={t('cueManual')}>
                    ·
                  </span>
                )}
                {manual && (
                  <>
                    <button type="button" className="btn small" disabled={disabled || !prevManual} onClick={() => swap(i, i - 1)} title="↑">
                      ▲
                    </button>
                    <button type="button" className="btn small" disabled={disabled || !nextManual} onClick={() => swap(i, i + 1)} title="↓">
                      ▼
                    </button>
                  </>
                )}
                <button type="button" className="btn small danger" disabled={disabled} onClick={() => remove(c.id)} title={t('storyVideoRemove')}>
                  ✖
                </button>
              </div>
              {c.kind === 'video' ? (
                <div className="sb-cue-body">
                  {c.mediaAssetId ? (
                    <>
                      <img className="sb-thumb" src={`/api/media/${encodeURIComponent(c.mediaAssetId)}/thumb`} alt="" loading="lazy" onError={(e) => ((e.currentTarget as HTMLImageElement).style.visibility = 'hidden')} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="media-card-title" title={c.media?.filename ?? c.mediaAssetId}>
                          {c.media?.title ?? c.mediaAssetId}
                        </div>
                        <div className="muted">
                          {c.media ? sec(c.media.durationMs) : ''} {c.media && <span className={`status ${c.media.status === 'READY' ? 'ok' : 'warn'}`}>{c.media.status}</span>}
                          {c.mediaCode && <span className="muted"> · {c.mediaCode}</span>}
                        </div>
                      </div>
                      <button type="button" className="btn small primary" onClick={() => onPreview(c)} title={t('cuePreview')}>
                        ▶
                      </button>
                      <button type="button" className="btn small" disabled={disabled} onClick={() => onChoose(c)} title={t('storyVideoChoose')}>
                        ✎
                      </button>
                    </>
                  ) : c.mediaCode ? (
                    <>
                      <span className="cue-code">⌛ {c.mediaCode}</span>
                      <span className="muted" style={{ flex: 1 }}>
                        {t('virtualBadge')}
                      </span>
                      <button type="button" className="btn small" onClick={() => void navigator.clipboard?.writeText(c.mediaCode ?? '')} title={t('copyCode')}>
                        ⧉
                      </button>
                      <button type="button" className="btn small" disabled={disabled} onClick={() => onChoose(c)}>
                        {t('storyVideoChoose')}
                      </button>
                    </>
                  ) : (
                    <>
                      <button type="button" className="btn small" disabled={disabled} onClick={() => onChoose(c)}>
                        ＋ {t('storyVideoChoose')}
                      </button>
                      <button type="button" className="btn small" disabled={disabled} onClick={() => onCommit(list().map((x) => (x.id === c.id ? { ...x, mediaAssetId: null, mediaCode: 'auto' } : x)))}>
                        ⌛ {t('storyVideoVirtual')}
                      </button>
                    </>
                  )}
                </div>
              ) : (
                <CgCueEditor draft={drafts_(c)} locale={locale} disabled={disabled} onEdit={(d) => edit(c.id, d)} onFlush={flush} />
              )}
            </li>
          );
        })}
      </ol>
      <div className="sb-foot">
        <button type="button" className="btn small" disabled={disabled} onClick={() => onChoose(null)}>
          ＋ {t('cueAddVideo')}
        </button>
        <button type="button" className="btn small" disabled={disabled} onClick={addVirtual}>
          ⌛ {t('storyVideoVirtual')}
        </button>
        <button type="button" className="btn small" disabled={disabled} onClick={addCg}>
          ＋ {t('cueAddCg')}
        </button>
        <span style={{ flex: 1 }} />
        <span className="muted" title={t('cueTotal')}>
          {videoMs > 0 ? `🎬 ${sec(videoMs)}` : ''}
        </span>
        {busy ? <span className="status info">…</span> : notice ? <span className="status info">{notice}</span> : null}
      </div>
      <p className="muted small" style={{ padding: '0 10px 8px', lineHeight: 1.4 }}>
        {t('cuePlaylistHint')}
      </p>
    </aside>
  );
}
